<template>
    <div class="col-12 mx-auto">
        <div class="btn-toolbar justify-content-between mt-sm-1 mt-md-3 mb-1" role="toolbar">
            <div class="form-inline col-lg-6 col-md-12">
                <span v-if="showAccommodationRequestsGridSettings">
                    <i class="fas fa-cogs pointer fa-2x float-right text-primary"
                       @click.stop="changeGridSettings"
                       data-toggle="tooltip"
                       :title="settingsTooltipText"></i>
                </span>
            </div>

            <div class="input-group" :class="showAccommodationRequestsGridSettings? 'col-lg-6 col-md-12':'col-12'">
                <input v-model="searchQuery" v-on:keyup.enter.stop="massSearch" v-on:keyup.esc.stop="clearFilters"
                       type="text" class="form-control" aria-label="Mass search input" aria-describedby="massSearch">
                <button class="btn btn-secondary" @click.stop="massSearch">
                    <i class="fas fa-search"></i>
                    {{ $t('buttons.search.btnText') }}
                </button>
                <button class="btn btn-secondary"
                        @click.stop="clearFilters">
                    <i class="fas fa-times"></i>
                    {{ $t('buttons.clearFilters.btnText') }}
                </button>
            </div>
        </div>

        <div v-if="showAccommodationRequestsGridSettings">
            <GridUserSettings ref="accommodationRequestsGridSettings"
                              :visible="showAccommodationRequestsGridSettingsForm"
                              :grid="$refs['accommodationReguestsGrid']"
                              :componentName="componentName">
            </GridUserSettings>
        </div>

        <div class="grid-wrapper">
            <kendo-datasource ref="datasource1"
                              :transport="transportSettings"
                              :schema-model-id="'id'"
                              :schema-model-fields="schemaModelFields"
                              :schema-total="'total'"
                              :schema-data="'data'"
                              :page-size='gridOptions.pageable.size'
                              :server-paging="true"
                              :server-sorting="true"
                              :server-filtering="true"
                              :error="dataSourceError">
            </kendo-datasource>

            <kendo-grid ref="accommodationReguestsGrid"
                        data-source-ref="datasource1"
                        :pageable="gridOptions.pageable"
                        :navigatable="gridOptions.navigatable"
                        :editable="gridOptions.editable"
                        :filterable="gridOptions.filterable"
                        :reorderable="gridOptions.reorderable"
                        :resizable="gridOptions.resizable"
                        :groupable="gridOptions.groupable"
                        :allow-copy="gridOptions.allowCopy"
                        :sortable-mode="gridOptions.sortable.mode"
                        :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                        :sortable-show-indexes="gridOptions.sortable.showIndexes"
                        :column-menu="gridOptions.columnMenu"
                        :selectable="gridOptions.selectable"
                        :scrollable="gridOptions.scrollable"
                        :toolbar="toolbar"
                        :excel="gridOptions.excel"
                        :data-bound="dataBound">
                <kendo-grid-column v-if="hasEditRights"
                                   :template="btnsTemplate"
                                   field="btnsColumn"
                                   title=" "
                                   :sortable="false"
                                   :filterable="false"
                                   :width="80"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'participationDetailsId'"
                                   :filterable="false"
                                   :title="$t('accommodationRequest.gridHeaders.id')"
                                   :width="100"
                                   :hidden="true"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'fullName'"
                                   :title="$t('accommodationRequest.gridHeaders.name')"
                                   :width="250"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'email'"
                                   :title="$t('accommodationRequest.gridHeaders.email')"
                                   :width="200"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'note'"
                                   :title="$t('accommodationRequest.gridHeaders.note')"
                                   :width="200"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'confirmedByAdmin'"
                                   :title="$t('accommodationRequest.gridHeaders.confirmedByAdmin')"
                                   :width="150"
                                   :template="confirmedByAdminCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'transportationRequired'"
                                   :title="$t('accommodationRequest.gridHeaders.transportationRequired')"
                                   :width="200"
                                   :template="transportationRequiredCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'vegetarianMenuRequired'"
                                   :title="$t('accommodationRequest.gridHeaders.vegetarianMenuRequired')"
                                   :width="200"
                                   :template="vegetarianMenuRequiredCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'accommodationRequired'"
                                   :title="$t('accommodationRequest.gridHeaders.accommodationRequired')"
                                   :width="200"
                                   :template="accommodationRequiredCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'fromUtc'"
                                   :title="$t('accommodationRequest.gridHeaders.fromDate')"
                                   :width="150"
                                   :format="customDateFormat"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'toUtc'"
                                   :title="$t('accommodationRequest.gridHeaders.toDate')"
                                   :width="150"
                                   :format="customDateFormat"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'accommodationPlaceName'"
                                   :title="$t('accommodationRequest.gridHeaders.accommodationPlaceName')"
                                   :width="200"
                                   :hidden="isEnVersion"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'accommodationPlaceNameEn'"
                                   :title="$t('accommodationRequest.gridHeaders.accommodationPlaceName')"
                                   :width="200"
                                   :hidden="!isEnVersion"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'accommodationPlaceNameCity'"
                                   :title="$t('accommodationRequest.gridHeaders.accommodationPlaceNameCity')"
                                   :width="200"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'single'"
                                   :title="$t('accommodationRequest.gridHeaders.single')"
                                   :width="200"
                                   :template="singleCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'double'"
                                   :title="$t('accommodationRequest.gridHeaders.double')"
                                   :width="200"
                                   :template="doubleCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'triple'"
                                   :title="$t('accommodationRequest.gridHeaders.triple')"
                                   :width="200"
                                   :template="tripleCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column :field="'apartment'"
                                   :title="$t('accommodationRequest.gridHeaders.apartment')"
                                   :width="200"
                                   :template="apartmentCellTemplate"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column field="lastUpdateDateUtc"
                                   :title="$t('accommodationRequest.gridHeaders.lastUpdateDate')"
                                   :width="180"
                                   :format="customDateTimeFormat"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
                <kendo-grid-column field="lastUpdater"
                                   :title="$t('accommodationRequest.gridHeaders.lastUpdater')"
                                   :width="200"
                                   :lockable="true"
                                   :locked="false">
                </kendo-grid-column>
            </kendo-grid>
        </div>
        <ConfirmationEditModal v-model="confirmationModel" :show="showConfirmation" @onCreate="onConfirmationUpdate" @onClose="onConfirmationClose"></ConfirmationEditModal>
    </div>
</template>
<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip'
    import GridUserSettings from "@/components/grid/GridSettingsWrapper";
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import { TempRankingToPersonModel } from '@/models/models';
    import ConfirmationEditModal from "@/components/dynamic/events/accommodationRequest/edit.modal";
    import participantService from '@/services/participant.service';
    import { EventClaims } from "@/models/enums";
    import { mapGetters } from 'vuex';

    export default {
        name: 'accommodation-requests-grid',
        components: {
            GridUserSettings,
            ConfirmationEditModal
        },
        created: function () {
            window.JSZip = JSZip
        },
        props: {
            showSettingsBtn: {
                default: true,
                type: Boolean
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            eventId: {
                type: Number,
                default: 0,
            },
            componentName: {
                default: 'AccommodationRequestsGrid',
                type: String
            },
        },
        data() {
            return {
                showAccommodationRequestsGridSettings: false,
                showAccommodationRequestsGridSettingsForm: false,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                searchQuery: '',
                schemaModelFields: {
                    participationDetailsId: { name: 'participationDetailsId', type: 'number', editable: false, nullable: false, visible: true },
                    fromUtc: { name: 'fromUtc', type: 'date', editable: false, nullable: true, visible: true },
                    toUtc: { name: 'toUtc', type: 'date', editable: false, nullable: true, visible: true },
                    fullName: {
                        name: 'fullName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    email: {
                        name: 'email',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    accommodationPlaceId: {
                        name: 'accommodationPlaceId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    accommodationPlaceName: { name: 'accommodationPlaceName', type: 'string', editable: false, nullable: true, visible: true },
                    accommodationPlaceNameEn: { name: 'accommodationPlaceNameEn', type: 'string', editable: false, nullable: true, visible: true },
                    accommodationPlaceNameCity: { name: 'accommodationPlaceNameCity', type: 'string', editable: false, nullable: true, visible: true },
                    note: { name: 'note', type: 'string', editable: false, nullable: true, visible: true },
                    single: { name: 'single', type: 'boolean', editable: false, nullable: true, visible: true },
                    double: { name: 'double', type: 'boolean', editable: false, nullable: true, visible: true },
                    triple: { name: 'triple', type: 'boolean', editable: false, nullable: true, visible: true },
                    apartment: { name: 'apartment', type: 'boolean', editable: false, nullable: true, visible: true },
                    accommodationRequired: { name: 'accommodationRequired', type: 'boolean', editable: false, nullable: true, visible: true },
                    vegetarianMenuRequired: { name: 'vegetarianMenuRequired', type: 'boolean', editable: false, nullable: true, visible: true },
                    transportationRequired: { name: 'transportationRequired', type: 'boolean', editable: false, nullable: true, visible: true },
                    confirmedByAdmin: { name: 'confirmedByAdmin', type: 'boolean', editable: false, nullable: true, visible: true },
                    lastUpdateDateUtc: { name: 'lastUpdateDateUtc', type: 'date', editable: false, nullable: true, visible: true },
                    lastUpdater: { name: 'lastUpdater', type: 'string', editable: false, nullable: true, visible: true },
                    isUpdated: { name: 'isUpdated', type: 'boolean', editable: false, nullable: true, visible: false }
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    toolbar: ['excel'],
                    excel: {
                        fileName: 'AccommodationRequests.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/participant/accommodationRequests',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                working: false,
                personModel: null,
                showPersonDetails: false,
                formEntryDetailsModel: null,
                showFormEntryDetails: false,
                showConfirmation: false,
                confirmationModel: null,
            }
        },
        methods: {
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            dataBound(e) {
                if (e.sender.options.dataSourceRef == 'datasource1'
                    && this.showSettingsBtn
                    && !this.showAccommodationRequestsGridSettings) {
                    this.showAccommodationRequestsGridSettings = true;
                }

                var items = e.sender.items();
                const that = this;
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.isUpdated) {
                        $(item).addClass('left-border-danger');
                    }
                });
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {

                    const newOptions = { eventId: this.eventId, request: options };
                    return kendo.stringify(newOptions);
                }
            },
            changeGridSettings() {
                this.showAccommodationRequestsGridSettingsForm = !this.showAccommodationRequestsGridSettingsForm;
            },
            refresh() {
                const grid = this.$refs.accommodationReguestsGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.accommodationReguestsGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.accommodationReguestsGrid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            confirmedByAdminCellTemplate: function (dataItem) {
                if (dataItem.confirmedByAdmin === undefined || dataItem.confirmedByAdmin === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.confirmedByAdmin ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.confirmedByAdmin ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            transportationRequiredCellTemplate: function (dataItem) {
                if (dataItem.transportationRequired === undefined || dataItem.transportationRequired === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.transportationRequired ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.transportationRequired ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            accommodationRequiredCellTemplate: function (dataItem) {
                if (dataItem.accommodationRequired === undefined || dataItem.accommodationRequired === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.accommodationRequired ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.accommodationRequired ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            vegetarianMenuRequiredCellTemplate: function (dataItem) {
                if (dataItem.vegetarianMenuRequired === undefined || dataItem.vegetarianMenuRequired === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.vegetarianMenuRequired ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.vegetarianMenuRequired ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            singleCellTemplate: function (dataItem) {
                if (dataItem.single === undefined || dataItem.single === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.single ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.single ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            doubleCellTemplate: function (dataItem) {
                if (dataItem.double === undefined || dataItem.double === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.double ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.double ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            tripleCellTemplate: function (dataItem) {
                if (dataItem.triple === undefined || dataItem.triple === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.triple ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.triple ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            apartmentCellTemplate: function (dataItem) {
                if (dataItem.apartment === undefined || dataItem.apartment === null) return '';

                let templateHtml =
                    '<span class="badge ' + (dataItem.apartment ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.apartment ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: "edit",
                                text: this.$t('buttons.edit.btnText'),
                                tooltip: this.$t('buttons.edit.btnText'),
                                class: 'btn btn-success',
                                icon: "fas fa-edit",
                                show: this.hasEditRights,
                                clickHandler: this.editBtnClick
                            },
                        ]
                    }
                }
            },
            editBtnClick(args) {
                participantService.getParticipationDetailsById(args.participationDetailsId)
                    .then(response => {
                        this.confirmationModel = new TempRankingToPersonModel(response.data);
                        this.showConfirmation = true
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            reload() {
                let grid = this.$refs.accommodationReguestsGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.read()
                }
            },
            onConfirmationUpdate(model) {
                this.reload();
            },
            onConfirmationClose() {
                this.showConfirmation = false;
                this.confirmationModel = null;
            },
        },
        mounted() {
        },
        computed:
        {
            ...mapGetters(['dateFormat', 'dateTimeFormat', 'isEnVersion', 'authToken', 'eventClaims']),
            customDateFormat() {
                return `{0: ${this.dateFormat}}`;
            },
            customDateTimeFormat() {
                return `{0: ${this.dateTimeFormat}}`;
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
            hasEditRights() {
                const claim = this.eventClaims[EventClaims.TempRanking];
                if (!claim) {
                    return false;
                }

                if (claim.create || claim.update) {
                    return true;
                }
                return false;
            },
        }
    }
</script>


<style lang="scss">
    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: 'DejaVu Sans', 'Arial', sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit,
    .k-grid-details,
    .k-grid-delete,
    .k-grid-pdf,
    .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>
